import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';

// TELEMETRY ONLY (ENG visibility measurement).
// The in-store player runs on iPads (iOS/WebKit), which suspends/throttles
// background tabs. We need to know whether a store's player actually stays
// FOREGROUNDED during business hours. This module observes the Page Visibility
// API and records each foreground/background transition to Sentry. It does not
// touch playback, retry, or stall logic — it only measures.

// A `player.visibility` event is emitted only when the state we just LEFT lasted
// at least this long. Rapid flapping (<1s) — e.g. an iOS lock/unlock bounce, or a
// bfcache pageshow that immediately follows a pagehide — collapses to breadcrumbs
// only, so a flapping session can't blow up Sentry event volume. Breadcrumbs are
// cheap and always recorded; the queryable captureMessage event is gated.
const VISIBILITY_MIN_EVENT_MS = 1_000;

// One id per page load, generated once at module init. Lets a store's visibility
// transitions be stitched into a single foreground/background timeline in Sentry
// without introducing a heavier session concept than this telemetry needs. (The
// player has no pre-existing client-side session id — sessionTokenRef is the
// server's single-session token, not a per-load telemetry id.)
const VISIBILITY_SESSION_ID =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `vis-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

type VisibilityState = 'visible' | 'hidden';

/**
 * Records Page Visibility transitions for the store player to Sentry.
 *
 * On every transition it emits a cheap breadcrumb (category `visibility`, data
 * `{ from, to, durationMs }`) and — when the previous state lasted at least
 * VISIBILITY_MIN_EVENT_MS — a queryable `player.visibility` info event tagged
 * with the store and the new visibility_state. The store name is passed in and
 * also mirrored onto the global `store` tag by the Player component, so events
 * are filterable per store.
 *
 * @param storeName current store name (may be '' until the store info loads)
 */
export function useVisibilityTelemetry(storeName: string): void {
  // Read the latest store name inside the (long-lived) event handlers without
  // re-subscribing the listeners each time it changes. Synced in an effect (not
  // during render) to match the rest of the Player's ref bookkeeping.
  const storeRef = useRef(storeName);
  useEffect(() => {
    storeRef.current = storeName;
  }, [storeName]);

  useEffect(() => {
    // Use wall-clock Date.now(), NOT performance.now(): the performance clock
    // freezes while the tab is in iOS bfcache / suspended, so a long backgrounded
    // period would report as ~0ms and get dropped by the >=1s throttle below —
    // which is exactly the case this telemetry exists to catch. NTP drift is
    // negligible over business-hour foreground/background windows.
    let lastState: VisibilityState = document.hidden ? 'hidden' : 'visible';
    let lastAt = Date.now();

    const record = (to: VisibilityState) => {
      const from = lastState;
      // visibilitychange + pagehide/pageshow can both fire for one edge; ignore
      // the redundant no-op transition so an incident isn't double-counted.
      if (to === from) return;
      const now = Date.now();
      const durationMs = Math.round(now - lastAt);
      lastState = to;
      lastAt = now;

      Sentry.addBreadcrumb({
        category: 'visibility',
        level: 'info',
        message: `visibility ${from} -> ${to}`,
        data: { from, to, durationMs },
      });

      // Emit the queryable event only when the store is known (mirrors the
      // `if (storeName)` guard on the setTag in index.tsx) AND the previous state
      // lasted past the throttle threshold (skips sub-second flapping noise).
      if (!storeRef.current || durationMs < VISIBILITY_MIN_EVENT_MS) return;
      Sentry.captureMessage('player.visibility', {
        level: 'info',
        tags: { store: storeRef.current, visibility_state: to },
        extra: { from, durationMs, sessionId: VISIBILITY_SESSION_ID },
      });
    };

    const onVisibilityChange = () => record(document.hidden ? 'hidden' : 'visible');
    // iOS bfcache / suspend edges don't reliably fire visibilitychange: pagehide
    // is the dependable "going away" signal and pageshow the "coming back" one.
    const onPageHide = () => record('hidden');
    const onPageShow = () => record(document.hidden ? 'hidden' : 'visible');

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);
}
